<script setup lang="ts">
// Vendor
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { format, addMinutes, isBefore, isEqual } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";

// Utils
import { formatDate, formatDistance } from "@citizenm/foundation/utils/date";

const { locale } = useI18n();

const {
  start = "07:30",
  end = "23:00",
  minimumDuration = 240,
  interval = 30,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
} = defineProps<{
  start?: string;
  end?: string;
  minimumDuration?: number; // minimum duration in minutes
  interval?: number; // length of each time slot in minutes
  timeZone?: string;
}>();

const arrival = defineModel<string>("arrival");
const departure = defineModel<string>("departure");

const today = new Date();
const baseDateStr = format(today, "yyyy-MM-dd");

const startTimeSlots = computed(() => {
  const startTZ = zonedTimeToUtc(`${baseDateStr}T${start}`, timeZone);
  const endTZ = zonedTimeToUtc(`${baseDateStr}T${end}`, timeZone);

  // Calculate the latest possible start time to meet the minimum duration
  const latestStartTime = addMinutes(endTZ, -minimumDuration);

  const options = [];
  let current = startTZ;

  while (isBefore(current, endTZ)) {
    const zoned = utcToZonedTime(current, timeZone);
    options.push({
      label: formatDate(zoned, "p", locale),
      value: format(zoned, "HH:mm"),
      disabled: isBefore(latestStartTime, current), // Disable times that can't meet the minimum duration
    });
    current = addMinutes(current, interval);
  }

  return options;
});

const endTimeSlots = computed(() => {
  const startTZ = zonedTimeToUtc(`${baseDateStr}T${start}`, timeZone);
  const endTZ = zonedTimeToUtc(`${baseDateStr}T${end}`, timeZone);

  // Parse the arrival time and calculate the minimum valid end time
  const arrivalTZ = zonedTimeToUtc(`${baseDateStr}T${arrival.value}`, timeZone);
  const minStartPlusDuration = addMinutes(arrivalTZ, minimumDuration);

  const options = [];
  let current = startTZ;

  while (isBefore(current, endTZ)) {
    const zoned = utcToZonedTime(current, timeZone);

    options.push({
      label: `${formatDate(zoned, "p", locale)} ${!isBefore(current, arrivalTZ) && !isEqual(current, arrivalTZ) ? `(${formatDistance(arrivalTZ, zoned, locale)})` : ""}`,
      value: format(zoned, "HH:mm"),
      disabled: isBefore(current, minStartPlusDuration),
    });
    current = addMinutes(current, interval);
  }

  options.push({
    label: `${formatDate(endTZ, "p", locale)} (${formatDistance(arrivalTZ, endTZ, locale)})`,
    value: format(endTZ, "HH:mm"),
    disabled: false,
  });

  return options;
});

watch(arrival, (newArrival) => {
  const arrivalTZ = zonedTimeToUtc(`${baseDateStr}T${newArrival}`, timeZone);
  const minStartPlusDuration = addMinutes(arrivalTZ, minimumDuration);

  if (
    !departure.value ||
    isBefore(
      zonedTimeToUtc(`${baseDateStr}T${departure.value}`, timeZone),
      minStartPlusDuration,
    )
  ) {
    departure.value = formatDate(minStartPlusDuration, "HH:mm");
  }
});
</script>

<template>
  <div class="timerange-input">
    <FormControl
      :label="$t('website.societym.panel_startTime')"
      name="start-time"
      input-type="select"
      class="timerange-input__control"
      data-testid="form-control-start-time"
    >
      <FormSelect
        :placeholder="$t('website.societym.panel_startTime')"
        :options="startTimeSlots"
        v-model="arrival"
        class="timerange-input__select"
        data-testid="form-select-start-time"
      />
      <IconTime
        class="timerange-input__icon"
        data-testid="form-icon-start-time"
      />
    </FormControl>

    <FormControl
      :label="$t('website.societym.panel_endTime')"
      name="end-time"
      input-type="select"
      class="timerange-input__control"
      data-testid="form-control-end-time"
    >
      <FormSelect
        :placeholder="$t('website.societym.panel_endTime')"
        :options="endTimeSlots"
        v-model="departure"
        class="timerange-input__select"
        data-testid="form-select-end-time"
      />
      <IconTime
        class="timerange-input__icon"
        data-testid="form-icon-start-time"
      />
    </FormControl>
  </div>
</template>

<style lang="scss">
.timerange-input {
  display: grid;
  grid-template-columns: subgrid;

  &__control {
    position: relative;
  }

  &__select {
    appearance: none;

    &.form-select {
      padding-inline-end: calc(
        1rem + 21px + 1rem
      ); // padding-right + icon width + padding-left
    }
  }

  &__icon {
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-50%);
    pointer-events: none;
  }
}
</style>
